import type { ReactNode } from 'react';
import { CalendarClock } from 'lucide-react';
import { PoweredByNotifi } from '@/components/public/PoweredByNotifi';
import { ServiceFeeNote } from '@/components/organisms/payments/ServiceFeeNote';
import { MembershipOfferBlock } from '@/components/organisms/estimates/MembershipOfferBlock';
import { formatRecurringParts } from '@/lib/sales/recurring-format';
import { fmt, useQuoteActions } from './quoteActionContext';
import { OptionalSelectionSlot } from './quoteSlots/OptionalSelectionSlot';
import {
  EstimateDocumentRenderer,
  type EstimateDocTotalsOverride,
} from '@/components/documents/estimates/blocks/EstimateDocumentRenderer';
import {
  buildDefaultEstimateLayout,
  ensureEstimateScopeBlock,
} from '@/components/documents/estimates/templateBuilder/defaultLayout';
import { paymentPlanRows } from '@/lib/quote/paymentPlanRows';

/**
 * The itemized estimate document — the customer-facing quote rendered through
 * the shared block engine from the tenant's resolved DESIGN (the stock default
 * design when none resolved). The single-scope presentation is the other
 * customer document; there is no classic hardcoded layout any more.
 *
 * The money/action layer is presentation-agnostic, so this reuses the shared
 * `actionPanel` (deposit + approve/decline/download, wired to
 * `useQuoteActions()`) rather than re-implementing it — only the DOCUMENT
 * markup (letterhead, billing details, bordered line-item table, plain totals,
 * terms) is the legacy layout.
 */
export function ItemizedEstimateDocument({ actionPanel }: { actionPanel: ReactNode }) {
  const {
    estimate,
    selectedEstimate,
    company,
    customFields,
    timezone,
    estimateDiscountLabel,
    attachmentImages,
    resolvedDocLayout,
    paymentPlan,
    serviceFee,
    subtotalWithoutSetupFee,
    approvalGrandTotal,
    approvalTaxTotal,
    displayMembershipSavings,
    membershipSavingsPlanNames,
    membershipPlanPrice,
    membershipSetupFee,
    selectedDiscountTotal,
    selectedDeposit,
    membershipOffer,
    acceptMembershipOffer,
    setAcceptMembershipOffer,
    selectedMembershipBillingOptionId,
    setSelectedMembershipBillingOptionId,
    taxBreakdown,
    showServiceFeeNote,
  } = useQuoteActions();

  // The document DESIGN governs ONLY the left document column; the right action
  // sidebar (summary, financing, download/approve/decline) stays identical for
  // every presentation. When a design resolves, the left column renders through
  // the shared block engine — the SAME engine as the builder preview and the PDF —
  // so what the partner designs is exactly what the customer sees. The design's
  // interaction slots draw nothing (renderSlots={false}); the sidebar owns those.
  const designLayout =
    resolvedDocLayout &&
    Array.isArray(resolvedDocLayout.blocks) &&
    resolvedDocLayout.blocks.length > 0
      ? ensureEstimateScopeBlock(resolvedDocLayout)
      : ensureEstimateScopeBlock(buildDefaultEstimateLayout());
  const docAttachments = attachmentImages.map((a) => ({
    id: a.id,
    url: a.downloadUrl,
    fileName: a.fileName,
  }));
  // Feed the design document the SAME authoritative totals the action sidebar
  // shows, so the two never disagree — the membership breakdown (list subtotal,
  // savings, separate plan-price line, member tax, member grand total) matches
  // the sidebar exactly. When membership is accepted the per-tax-code
  // breakdown is collapsed to a single member-tax line, mirroring the sidebar.
  const totalsOverride: EstimateDocTotalsOverride = {
    subtotal: subtotalWithoutSetupFee,
    membershipSavings: displayMembershipSavings,
    membershipPlanPrice,
    membershipSetupFee,
    membershipPlanLabel: membershipOffer?.planName ?? 'Membership',
    ...(membershipSavingsPlanNames ? { membershipSavingsLabel: membershipSavingsPlanNames } : {}),
    discountTotal: selectedDiscountTotal,
    discountLabel: estimateDiscountLabel,
    taxBreakdown: acceptMembershipOffer ? [] : taxBreakdown,
    taxTotal: approvalTaxTotal,
    grandTotal: approvalGrandTotal,
    depositAmount: selectedDeposit,
  };
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-8 lg:flex-row">
          {/* Sidebar (deposit + actions) — fixed for every design. */}
          <div
            id="estimate-actions"
            className="order-2 mx-auto w-full min-w-0 max-w-xl flex-shrink-0 scroll-mt-8 lg:mx-0 lg:max-w-none lg:sticky lg:top-8 lg:w-[340px] lg:self-start"
          >
            {actionPanel}
          </div>
          {/* Document — rendered through the design engine (matches the preview).
                The renderer already draws its own sheet (white card + shadow), so
                there is NO wrapper card here — otherwise the document sits in a
                doubled card. */}
          <div className="order-1 min-w-0 flex-1">
            {/* Scope of work is a design BLOCK, rendered inside the document at
                  its authored position. `ensureEstimateScopeBlock` above injects
                  it into older layouts that predate the block, so it is never
                  lost — no app-owned fallback needed here. */}
            <EstimateDocumentRenderer
              layout={designLayout}
              // The SELECTION-aware estimate (optional add-ons the customer
              // toggled), NOT the raw estimate — so the design document tracks
              // the live sale state, exactly like the action sidebar. The
              // renderer re-prices member lines itself from `membershipOffer`.
              estimate={selectedEstimate}
              company={company}
              attachmentImages={docAttachments}
              renderSlots={false}
              membershipOffer={membershipOffer}
              showMemberPrice={acceptMembershipOffer && membershipOffer?.planPrice != null}
              totalsOverride={totalsOverride}
              timeZone={timezone}
              // On the sheet, not after it. Rendered as a sibling this landed
              // below the document as a detached card, so the plan read as
              // unrelated to the estimate it prices.
              documentTrailing={
                !acceptMembershipOffer && paymentPlan?.stages?.length ? (
                  <PaymentPlanCard paymentPlan={paymentPlan} />
                ) : null
              }
              beforeTotals={
                <>
                  <OptionalSelectionSlot />
                  {membershipOffer ? (
                    <div className="mt-6">
                      <MembershipOfferBlock
                        offer={membershipOffer}
                        selected={acceptMembershipOffer}
                        onSelectedChange={setAcceptMembershipOffer}
                        selectedBillingOptionId={selectedMembershipBillingOptionId}
                        onBillingOptionChange={setSelectedMembershipBillingOptionId}
                        variant="banner"
                        compact
                        className="w-full"
                      />
                    </div>
                  ) : null}
                </>
              }
            />
            {/* App-owned disclosures the design engine does not render but the
                  document must carry on every design. */}
            {showServiceFeeNote ? (
              <div className="mt-6">
                <ServiceFeeNote serviceFee={serviceFee} />
              </div>
            ) : null}
            {estimate.bookingType === 'RECURRING' && estimate.recurringConfig ? (
              <RecurringServiceCard config={estimate.recurringConfig} />
            ) : null}
            {customFields.length > 0 ? <CustomerCustomFields fields={customFields} /> : null}
          </div>
        </div>
        {/* Powered by Notifi — always shown in the link footer. */}
        <div className="mt-8 text-center">
          <PoweredByNotifi />
        </div>
      </div>
    </div>
  );
}

function PaymentPlanCard({
  paymentPlan,
}: {
  paymentPlan: NonNullable<ReturnType<typeof useQuoteActions>['paymentPlan']>;
}) {
  return (
    <div className="pt-[10px]" style={{ borderTop: '1px solid #E5E7EB' }}>
      <p className="mb-2 text-sm font-semibold text-[#2B373F]">Payment plan</p>
      {paymentPlan.previewText ? (
        <p className="mb-3 text-xs text-[#6B7280]">{paymentPlan.previewText}</p>
      ) : null}
      <div className="space-y-2">
        {/* Deposit included — the stages are resolved net of it. */}
        {paymentPlanRows(paymentPlan).map((row) => (
          <div key={row.key} className="flex justify-between gap-3 text-sm">
            <span className="min-w-0 text-[#374151]">
              <span className="font-medium text-[#2B373F]">{row.label}</span>
              <span className="text-[#6B7280]"> · {row.detail}</span>
            </span>
            <span className="flex-shrink-0 text-[#2B373F]">
              {row.amount == null
                ? 'Remaining balance'
                : `${fmt(row.amount)}${row.isRemaining ? ' (remaining)' : ''}`}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 h-px bg-gray-200" />
      <div className="mt-2 flex justify-between text-sm font-semibold text-[#2B373F]">
        <span>Total</span>
        <span>{fmt(paymentPlan.grandTotal)}</span>
      </div>
    </div>
  );
}

function RecurringServiceCard({ config }: { config: Parameters<typeof formatRecurringParts>[0] }) {
  const parts = formatRecurringParts(config);
  return (
    <div className="mt-6 flex justify-end">
      <div className="w-72 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="mb-2 flex items-center gap-2">
          <CalendarClock className="size-4 text-[#2B373F]" />
          <h3 className="text-sm font-semibold text-[#2B373F]">Recurring service</h3>
        </div>
        <dl className="space-y-1.5 text-sm">
          {parts.details.map((detail) => (
            <div key={detail.label} className="flex justify-between gap-3">
              <dt className="text-gray-500">{detail.label}</dt>
              <dd className="text-right font-medium text-[#2B373F]">{detail.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

function CustomerCustomFields({
  fields,
}: {
  fields: ReturnType<typeof useQuoteActions>['customFields'];
}) {
  return (
    <div className="mt-6 rounded-lg bg-[#F9FAFB] p-6">
      <h3 className="mb-3 text-sm font-semibold text-[#2B373F]">Additional Details</h3>
      <dl className="space-y-2">
        {fields.map((cf) => (
          <div key={cf.fieldDefinitionId} className="flex gap-2 text-sm">
            <dt className="font-medium text-[#374151]">{cf.label}:</dt>
            <dd className="text-[#374151]">{cf.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
